<template>
  <div>
    <div class="u-mt4 u-mb4">
      <h2 class="secondary-header">{{ t('account.mfa') }}</h2>  
    </div>
    <el-switch
        :active-text="t('account.enable_mfa')"
        active-color="#5AB67F"
        inactive-color="#CB6B0B"
        @change="onMfaCheckBoxClick"
        :value='mfaCheckboxValue'
    >
    </el-switch>
    <div v-if="isEnablingMfa">
      <p class="u-mt3">{{ t('account.authenticator_app_step1') }}</p>
      <div class="center-qr">
        <qr-code
          :text="qrCode"
        />
      </div>
      <div class="l-flex-grid l-flex-grid--h-center">
        <el-button  class="el-button--primary u-text-uppercase" @click="copySecret">
          <fa-icon :icon="['far', 'copy']" class="u-mr2"></fa-icon> {{ secret }}
        </el-button>
      </div>
      <p>{{ t('account.authenticator_app_step2') }}</p>
      <el-input class="item-input-field" type="number" v-model="codeEntered" />
      <el-button class="el-button--primary u-text-uppercase u-width100" @click="handleTestMfaCode" :disabled='!codeEntered'>
        {{ t('account.verify_mfa') }}
      </el-button>
    </div>
  </div>
</template>

<script>

import {mapActions, mapGetters} from 'vuex'
import QrCode from 'vue-qrcode-component'

export default {
  name: 'multi-factor-authentication',
  components: {
    QrCode,
  },
  data () {
    return {
      isEnablingMfa: false,
      mfaCheckboxValue: false,
      qrCode: '',
      secret: '',
      codeEntered: ''
    }
  },
  watch: {
    isMfaEnabled: {
      handler (isMfaEnabled) {
        this.recalculateMfaCheckboxValue()
      },
      immediate: true
    },
    isMfaMandatoryMode: {
      handler (isMfaMandatoryMode) {
        this.recalculateMfaCheckboxValue()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('account', ['setupMfa', 'testMfaCode', 'setMfaEnabled']),
    onMfaCheckBoxClick () {
      if (this.mfaCheckboxValue) {
        if (this.isMfaMandatoryMode) {
          if (!this.isEnablingMfa) {
            this.$confirm(
              this.t('account.mfa_cannot_disable_open_qr_panel'),
              '',
              {
                confirmButtonText: this.t('account.mfa_open_qr_setup'),
                cancelButtonText: this.t('global.cancel'),
                showClose: false,
              }
            ).then(() => {
              this.updateEnableMfa(true)
            }).catch(() => {})
          } else {
            this.$elAlert(this.t('account.mfa_cannot_disable'), this.t('global.error'))
          }
          return
        }
        // it was true and now we're trying to turn off
        this.mfaCheckboxValue = false
        this.updateEnableMfa(false)
        return
      }
      // it was off, enable it
      this.$confirm(
        this.t('account.mfa_prompt_message_enable'),
        this.t('global.are_you_sure'),
        {
          confirmButtonText: this.t('global.ok'),
          cancelButtonText: this.t('global.cancel'),
          showClose: false,
        }
      ).then(() => {
        this.mfaCheckboxValue = true
        this.updateEnableMfa(true)
      }).catch(() => {})
    },
    setCodeEntered (value) {
      this.codeEntered = value
    },
    async updateEnableMfa (value) {
      if (value) {
        try {
          let response = await this.setupMfa({
            recreate: false,
            user_enabled: true,
            authenticator_enabled: false
          })
          const uri = response.data.uri
          this.qrCode = uri
          const urlObject = new URL(uri.replace('otpauth://', 'https://'))
          const secret = urlObject.searchParams.get('secret')
          this.secret = secret
          this.isEnablingMfa = true
          this.$elAlert(this.t('account.successfully_enabled_mfa'), this.t('global.notice'))
        } catch (e) {
          this.$elAlert(this.t('account.error_enabling_mfa'), this.t('global.error'))
          this.isEnablingMfa = false
        }
      } else {
        this.$confirm(
          this.t('account.confirm_disable_mfa'),
          this.t('global.are_you_sure')).then(() => {
            this.setupMfa({
              recreate: false,
              user_enabled: false,
            }).then(() => {
              this.setMfaEnabled(false)
              this.isEnablingMfa = false
              this.codeEntered = ''
              this.$elAlert(this.t('account.successfully_disabled_mfa'), this.t('global.notice'))
            }).catch(() => {
              this.$elAlert(this.t('account.error_disabling_mfa'), this.t('global.error'))
            })
          }).catch(() => { this.mfaCheckboxValue = true }
        )
      }
    },
    async copySecret () {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.secret)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = this.secret
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
      } catch (e) {
        console.error('Failed to copy secret', e)
      }
    },
    async handleTestMfaCode () {
      try {
        const response = await this.testMfaCode({
          mfa_code: this.codeEntered,
          mfa_code_type: 'totp'
        })
        if (response.data.valid) {
          await this.enableMfaAuthenticatorApp()
        } else {
          this.$elAlert(this.t('reset_password.mfa_is_expired_or_invalid'), this.t('global.error'))
        }
      } catch (error) {
        this.$elAlert(this.t('reset_password.mfa_is_expired_or_invalid'), this.t('global.error'))
      }
    },
    async enableMfaAuthenticatorApp () {
      try {
        await this.setupMfa({
          recreate: false,
          user_enabled: true,
          authenticator_enabled: true
        })
        this.isEnablingMfa = false
        this.setMfaEnabled(true)
        this.codeEntered = ''
        this.$elAlert(this.t('account.mfa_successfully_enabled_authenticator_app'), this.t('global.notice'))
      } catch (e) {
        this.$elAlert(this.t('account.error_enabling_mfa'), this.t('global.error'))
        this.isEnablingMfa = false
      }
    },
    recalculateMfaCheckboxValue () {
      this.mfaCheckboxValue = this.isMfaMandatoryMode || this.isMfaEnabled
    }
  },
  computed: {
    ...mapGetters('account', ['isMfaEnabled']),
    isMfaMandatoryMode () {
      return this.setting('authentication.mfa_mode') === 'mandatory'
    },
  },
}

</script>

<style scoped lang="scss">
@import '~styles/_variables.scss';

.center-qr {
  display: flex;
  justify-content: center;
  margin-top: 10px;
  margin-bottom: 26px;
}

</style>
